import {emotionStyled, tokens} from '@packages/utilities/styled';

import {
  activeSelector,
  disabledSelector,
  focusWithinSelector,
  hoverSelector,
} from '../../../utils';

import IconButton from '../../IconButton';
import Tag from '../../Tag';
import Text from '../../Text';

export const AutocompleteInputWrapper = emotionStyled('div')(
  {
    alignItems: 'stretch',
    backgroundColor: `$uiInputBackground`,
    boxShadow: '$inputInner',
    display: 'inline-flex',
    outline: `$uiInputBorderColor`,
    outlineOffset: '-1px', // Offset for the outline to be inside the border
    borderRadius: '$2',
    position: 'relative',
    width: '100%',

    [`&${focusWithinSelector}`]: {
      outline: '$blueBorder',
    },

    [`${disabledSelector('&')}`]: {
      background: '$backgroundInactive',
      opacity: tokens.opacities.disabled,
      cursor: 'not-allowed',
    },
  },
  {
    componentName: 'AutocompleteInput',
    variants: {
      isError: {
        true: {
          outline: '$redBorder',

          [`&${focusWithinSelector}`]: {
            outline: '$redBorder',
          },
        },
      },
    },
  }
);

export const AutocompleteInputFieldWrapper = emotionStyled('div')(
  {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    padding: tokens.ui.autocomplete.input.padding,
    minHeight: tokens.ui.autocomplete.input.height,
    minWidth: 0,
    width: '100%',
  },
  {
    componentName: 'AutocompleteInputFieldWrapper',
    variants: {
      isClearable: {
        true: {
          paddingRight: '$0',
        },
      },
    },
  }
);

// We introduce a grid to control the width of the input element
export const InputWrapper = emotionStyled('div')(
  {
    cursor: 'text',
    display: 'inline-grid',
    flex: '1 1 auto',
    gridTemplateColumns: '0 min-content',

    // We'll use this to display the input value
    ':after': {
      content: "attr(data-value) ' '",
      visibility: 'hidden',
      whiteSpace: 'pre',
      gridArea: '1/2',
      font: 'inherit',
      minWidth: '$1',
      height: tokens.ui.autocomplete.input.height,
    },
  },
  {
    variants: {
      isDisabled: {
        true: {
          cursor: 'not-allowed',
        },
      },
      // When the input sits between chips (reorderable caret), it must size to
      // its content instead of growing to fill the row — otherwise it pushes the
      // chips apart. At the end of the list it grows as usual.
      isInline: {
        true: {
          flex: '0 1 auto',
        },
      },
    },
  }
);

export const InputView = emotionStyled('input')(
  {
    background: 'none',
    border: 'none',
    color: '$textPrimary',
    fontSize: '$body',
    outline: 'none',
    padding: 0,
    width: '100%',

    // We're treating the input like a grid item to prevent it from wrapping onto the next line
    gridArea: '1/2',
    minWidth: '$1',

    [`${disabledSelector('&')}`]: {
      cursor: 'not-allowed',
    },
  },
  {
    variants: {
      shouldHideCursor: {
        true: {
          caretColor: 'transparent',
        },
      },
    },
  }
);

export const PlaceholderTextView = emotionStyled(Text)({
  alignContent: 'center',
  gridArea: '1/2',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

export const SelectedValueTextView = emotionStyled(Text)({
  color: '$textPrimary',
  minWidth: '0px',
  width: 'fit-content',
});

export const SelectedValueTagView = emotionStyled(Tag)(
  {},
  {
    variants: {
      isFocused: {
        true: {
          // Highlight the whole chip (not just the dismiss button) when it is
          // the active tag during keyboard navigation / reordering.
          outline: '$blueBorder',
          outlineOffset: '1px',
          borderRadius: '$2',
        },
      },
      variant: {
        default: {},
      },
    },
  }
);

// The keyboard/drag wrapper around each reorderable chip. It renders the
// picked-up (grabbed) state heavier than a merely-focused chip: a thicker ring
// plus a lift shadow so it's clear the chip is being moved.
export const SortableTagWrapper = emotionStyled('span')(
  {
    display: 'inline-flex',
    outline: 'none',
    // Tag caps itself with `max-width: 100%`, which resolves against its
    // containing block. This wrapper becomes that containing block, and a
    // shrink-to-fit inline-flex box sized by the Tag caps nothing — so a long
    // label would stop truncating and widen the row until it wraps. Carrying
    // the cap here hands the row's width back to the Tag.
    maxWidth: '100%',
  },
  {
    variants: {
      isGrabbed: {
        true: {
          outline: '$blueBorder',
          outlineWidth: '2px',
          outlineOffset: '2px',
          borderRadius: '$2',
          boxShadow: '$2',
        },
      },
    },
  }
);

// Marks the gap a dragged chip would land in if dropped. There's no
// live-shift preview (see noShiftSortingStrategy in AutocompleteInput.tsx),
// so without this, a wrapped-line drop has no feedback about where it'll
// land. Rendered as a real flex item interleaved into the chip row at the
// target gap (same interleaving the input caret already uses) rather than
// absolutely positioned against a chip — that way it gets the row's normal
// gap spacing on both sides instead of two competing edge markers.
export const DropIndicatorView = emotionStyled('span')({
  alignSelf: 'stretch',
  width: '2px',
  borderRadius: '$rounded',
  backgroundColor: '$blueBorder',
});

export const AutocompleteInputControlWrapper = emotionStyled('div')({
  display: 'inline-flex',
});

export const ClearButtonView = emotionStyled(IconButton)(
  {
    padding: tokens.ui.autocomplete.input.padding,

    [`&${hoverSelector}`]: {
      background: 'none',
    },

    [`&${activeSelector}`]: {
      background: 'none',
    },
  },
  {
    componentName: 'AutocompleteInputClearButton',
  }
);

export const AutocompleteTriggerButtonView = emotionStyled(IconButton)(
  {
    // Override IconButton borderRadius on left side
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    padding: tokens.ui.autocomplete.input.padding,

    // Apply left-hand divider
    ':not(:hover):after': {
      position: 'absolute',
      pointerEvents: 'none',
      top: 1,
      bottom: 1,
      left: 0,
      content: '""',
      background: '$border1',
      width: '$0.25',
    },

    // Override IconButton hover styles
    [`&${hoverSelector}`]: {
      background: 'none',
    },

    // Override IconButton active styles
    [`&${activeSelector}`]: {
      background: 'none',
    },

    // Reapply hover and active styles
    [`&${hoverSelector}:after, &${activeSelector}:after`]: {
      position: 'absolute',
      pointerEvents: 'none',
      top: 1,
      left: 0,
      right: 1,
      bottom: 1,
      content: '""',
      background: '$backgroundRaised',
      borderLeft: '$border1',
      borderRadius: '$0 $1 $1 $0',
    },
  },
  {
    componentName: 'AutocompleteInputDropdownButton',
    variants: {
      isOpen: {
        true: {
          '[data-sc="Icon"]': {
            color: '$textPrimary',
          },
          ':after, :not(:hover):after': {
            position: 'absolute',
            pointerEvents: 'none',
            top: 1,
            left: 0,
            right: 1,
            bottom: 1,
            content: '""',
            background: '$backgroundRaised',
            borderLeft: '$border1',
            borderRadius: '$0 $1 $1 $0',
            width: 'unset', // Override left-hand divider width that was set above
          },
        },
      },
    },
  }
);

export const AutocompleteInputFieldSelector =
  AutocompleteInputFieldWrapper.toString();
export const AutocompleteInputClearButtonSelector = ClearButtonView.toString();
export const AutocompleteInputDropdownButtonSelector =
  AutocompleteTriggerButtonView.toString();
